import { getApiUrl } from './api.config'

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  error?: string;
}

export interface GoogleLoginRequest {
  idToken: string;
}

export interface LinkAccountRequest {
  password: string;
  provider: string;
  providerToken: string;
}

export interface AuthResult {
  success: boolean;
  requiresLinking?: boolean;
  userId?: number;
  message?: string;
  authResponse?: AuthResponse;
}

// Centralized auth storage management
const authStorage = {
  setAuth: (token: string, user: any) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('currentUser', JSON.stringify(user)); // Keep for compatibility
  },
  
  clearAuth: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('currentUser');
  },
  
  getToken: () => localStorage.getItem('authToken'),
  
  getUser: () => {
    const userData = localStorage.getItem('user') || localStorage.getItem('currentUser');
    return userData ? JSON.parse(userData) : null;
  }
};

// API helper function as specified by backend
const apiCall = async (url: string, options: RequestInit = {}) => {
  const token = authStorage.getToken();
  const config: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
  };
  const response = await fetch(getApiUrl(url), config);
  if (response.status === 401) {
    // Token expired or invalid
    authStorage.clearAuth();
    throw new Error('Authentication required');
  }
  return response;
};

// Login function matching backend implementation
const login = async (email: string, password: string) => {
  const response = await fetch(getApiUrl('Auth/login'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });
  if (!response.ok) {
    if (response.status === 400) {
      const error = await response.json();
      throw new Error(error.error || 'Invalid login credentials');
    }
    throw new Error('Login failed');
  }
  const data = await response.json();
  // Store token and user data using centralized storage
  authStorage.setAuth(data.token, data.user);
  return data;
};

// Register function matching backend implementation
const register = async (email: string, name: string, password: string) => {
  // Ensure correct casing for Auth in the endpoint
  const response = await fetch(getApiUrl('Auth/register'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, name, password }),
  });
  if (!response.ok) {
    let errorMsg = 'Registration failed';
    try {
      const error = await response.json();
      errorMsg = error.error || error.message || errorMsg;
    } catch {
      // If not JSON, try to get text
      try {
        const text = await response.text();
        if (text) errorMsg = text;
      } catch {
        // Failed to get text, use default error message
      }
    }
    throw new Error(errorMsg);
  }
  const data = await response.json();
  // Store token and user data using centralized storage
  authStorage.setAuth(data.token, data.user);
  return data;
};

// Get current user function matching backend implementation
const getCurrentUser = async () => {
  const token = authStorage.getToken();
  if (!token) {
    throw new Error('No token found');
  }

  const response = await fetch(getApiUrl('Auth/me'), {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    // Clear invalid token
    authStorage.clearAuth();
    throw new Error('Failed to get user info');
  }

  const user = await response.json();
  return user;
};

export const auth = {
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    return login(credentials.email, credentials.password);
  },

  register: async (userData: RegisterRequest): Promise<AuthResponse> => {
    return register(userData.email, userData.name, userData.password);
  },

  logout: async (): Promise<void> => {
    try {
      const response = await apiCall('/Auth/logout', {
        method: 'POST',
      });

      if (!response.ok) {
        console.warn('Logout request failed, but continuing with local cleanup');
      }
    } catch {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed, but continuing with local cleanup');
    } finally {
      authStorage.clearAuth();
    }
  },

  getCurrentUser: async () => {
    return getCurrentUser();
  },

  validateToken: async (): Promise<AuthResponse> => {
    const user = await getCurrentUser();
    const token = authStorage.getToken();
    return {
      token: token!,
      user: user
    };
  },

  refreshToken: async (): Promise<AuthResponse> => {
    const response = await apiCall('/Auth/refresh', {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = await response.json();

    // Update stored token using centralized storage
    authStorage.setAuth(data.token, data.user || authStorage.getUser());

    return data;
  },

  requestPasswordReset: async (email: string): Promise<void> => {
    const response = await fetch(getApiUrl('Auth/request-password-reset'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      let errorMessage = 'Failed to send password reset email';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If we can't parse the error, use the default message
      }
      throw new Error(errorMessage);
    }
  },

  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    const response = await fetch(getApiUrl('Auth/reset-password'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, newPassword }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to parse error response' }));
      
      // Create an enhanced error object that includes the response data
      const error = new Error(errorData.message || 'Failed to reset password') as any;
      error.response = {
        status: response.status,
        data: errorData
      };
      throw error;
    }
  },

  googleLogin: async (idToken: string): Promise<AuthResult> => {
    const response = await fetch(getApiUrl('Auth/google-login'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ idToken }),
    });

    if (response.ok) {
      const data = await response.json();
      // Store token and user data using centralized storage
      authStorage.setAuth(data.token, data.user);
      
      return {
        success: true,
        authResponse: data
      };
    } else if (response.status === 409) {
      // Account linking required
      const data = await response.json();
      return {
        success: false,
        requiresLinking: true,
        userId: data.userId,
        message: data.message || 'Account linking required'
      };
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Google login failed' }));
      throw new Error(errorData.message || 'Google login failed');
    }
  },

  linkAccount: async (userId: number, password: string, providerToken: string): Promise<void> => {
    const response = await fetch(getApiUrl(`Auth/link-account/${userId}`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        password,
        provider: 'google',
        providerToken
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to link account' }));
      throw new Error(errorData.message || 'Failed to link account');
    }
  }
};

// Export the apiCall helper and authStorage for use in other API modules
export { apiCall, authStorage };
